import React, { useEffect, useState } from 'react';
import API_URL from '../../config';
import { generateQRCode } from '../../utils/qrcode';
import PrintableHeader from './PrintableHeader';
import PrintableSignatures from './PrintableSignatures';
import EmittedByBlock from './EmittedByBlock';
import MarkdownText from './MarkdownText';
import { formatDataPtBR } from '../../utils/dateFormat';
import { formatCurrencyBR } from '../../utils/currency';
import { formatHipoteseAlternativaPorque } from '../../utils/porqueFormat';
import { cssSlug } from '../../utils/cssSlug';
import { ramo6MTemConteudo } from '../../utils/ishikawa';
import { isPlaceholderFisico, labelsFisicos } from '../../utils/setor';
import { labelDisposicao, labelSimNao } from '../../utils/iso87'; // #279/#280
import './ReportPrintable.css';

// Lê id da URL (path matching manual — sem react-router-dom)
function getNcIdFromPath() {
  const parts = (typeof window !== 'undefined' ? window.location.pathname : '').split('/');
  // Esperado: /relatorio-pdf/<id>
  return parts[2] || null;
}

function getEnabledSections() {
  if (typeof window === 'undefined') return null;
  const sp = new URLSearchParams(window.location.search);
  const raw = sp.get('sections');
  if (!raw || raw.trim() === '') return null; // null => todas
  return new Set(raw.split(',').map((s) => s.trim()).filter(Boolean));
}

// Status do validacao_status (campo separado de workflow_status, com vocabulário próprio)
const VALIDACAO_STATUS_LABEL = {
  nova:        'Nova',
  em_analise:  'Em Análise',
  em_execucao: 'Em Execução',
  concluida:   'Concluída',
};

export default function ReportPrintable() {
  const ncId = getNcIdFromPath();
  const enabled = getEnabledSections();
  const showSection = (key) => enabled === null || enabled.has(key);

  const [reportData, setReportData] = useState(null);
  const [tenantProfile, setTenantProfile] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Você precisa estar logado para gerar este relatório. Volte ao Qaa.sa e faça login.');
        setLoading(false);
        return;
      }
      if (!ncId) {
        setError('ID da NC ausente na URL.');
        setLoading(false);
        return;
      }

      try {
        // Fetch paralelo: dados da NC + perfil do tenant emissor
        const [reportRes, profileRes] = await Promise.all([
          fetch(`${API_URL}/api/v2/nc/${ncId}/relatorio-completo`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_URL}/api/account/profile`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (!reportRes.ok) {
          throw new Error(`HTTP ${reportRes.status}`);
        }
        const data = await reportRes.json();
        if (cancelled) return;
        setReportData(data);

        // Profile é best-effort: se falhar (rede, role sem permissão futura),
        // o bloco "Emitido por" simplesmente não renderiza. Não bloqueia o PDF.
        if (profileRes.ok) {
          try {
            const profile = await profileRes.json();
            if (!cancelled) setTenantProfile(profile);
          } catch (_) { /* ignora parse error */ }
        }

        // QR aponta para a NC no app autenticado (subdomínio app.qaasa.com.br)
        const qrTarget = `https://app.qaasa.com.br/nc/${ncId}`;
        const qr = await generateQRCode(qrTarget);
        if (!cancelled) setQrDataUrl(qr);
      } catch (err) {
        if (cancelled) return;
        setError(`Erro ao carregar relatório: ${err.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [ncId]);

  // Dispara window.print() automaticamente após dados carregarem
  // 500ms de buffer para CSS/imagens/QR terminarem de renderizar
  useEffect(() => {
    if (loading || !reportData || error) return;
    const timer = setTimeout(() => {
      try { window.print(); } catch (_) { /* ignore */ }
    }, 500);
    return () => clearTimeout(timer);
  }, [loading, reportData, error]);

  if (loading) {
    return <div className="printable-loading">Preparando relatório…</div>;
  }
  if (error) {
    return (
      <div className="printable-error">
        <p>{error}</p>
        <p>
          <a href="/">Voltar ao Qaa.sa</a>
        </p>
      </div>
    );
  }

  const dados_basicos = reportData.dados_basicos || {};
  const analise = reportData.analise || {};
  const empresa = reportData.empresa || {};
  const recorrencia = reportData.recorrencia || {};
  const score = reportData.graficos?.score_qualidade?.score ?? reportData.score_qualidade ?? 0;
  const av4 = reportData.analise_v4 || null;
  const planoLista = Array.isArray(reportData.plano_5w2h) ? reportData.plano_5w2h : [];
  const ganttRows = Array.isArray(reportData.gantt_data) ? reportData.gantt_data : [];

  // Cinco porques: aceita lista de strings OU objetos {pergunta, resposta}
  const porques = (analise.cinco_porques || [])
    .map((p) => (typeof p === 'object' && p !== null ? (p.resposta || p.pergunta || '') : (p || '')))
    .map((s) => String(s).trim())
    .filter(Boolean);

  /*
    Tier 1 Bug 3 fix (Sessão 14) — TD87: numeração dinâmica de seções.

    Antes: cada seção tinha `<span className="section-num">N</span>` hardcoded
    em 19 lugares (L164/L205/.../L634). Quando uma seção condicional sumia
    (ex: TD81 risco_recorrencia='indeterminado' → seção 16 não renderiza),
    a numeração saltava (ex: 15 → 17 confirmado empiricamente em
    RNC002_2026_MULTIELET.pdf v2 + v5).

    Fix cirúrgico cravado decisão Francis: closure `nextSectionNum()` que
    incrementa contador a cada chamada. Cada seção condicional chama
    `nextSectionNum()` dentro do bloco renderizado — seções que não
    renderizam (condicional false) não consomem número. Resultado:
    numeração sempre sequencial 1..N sem saltos.

    Refactor completo (array de seções renderizado em map) fica como
    TD-followup-refactor-sections-array Sprint 5+ se valer manutenção.
  */
  let _sectionCounter = 0;
  const nextSectionNum = () => {
    _sectionCounter += 1;
    return _sectionCounter;
  };

  return (
    <div className="printable-document">
      <PrintableHeader reportData={reportData} qrDataUrl={qrDataUrl} />

      <EmittedByBlock tenant={tenantProfile} />

      <main className="printable-content">
        {/* 1. Identificação */}
        {showSection('identificacao') && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Identificação
            </h2>
            <div className="info-grid">
              <div className="info-card">
                <div className="info-label">Tenant Emissor</div>
                <div className="info-value">{empresa.nome_fantasia || empresa.razao_social || '—'}</div>
              </div>
              {reportData.origem?.origem_tipo === 'cliente' && reportData.origem?.cliente_emissor && (
                <div className="info-card">
                  <div className="info-label">Cliente Reclamante</div>
                  <div className="info-value">{reportData.origem.cliente_emissor}</div>
                </div>
              )}
              {/* #264 — em setor de SERVIÇO, omite Produto/Lote físicos quando
                  vazios (não há produto/lote a identificar; "—" soava estranho).
                  Setor industrial: inalterado. is_servico vem do backend.
                  #264b — quando PREENCHIDOS num serviço, renomeia os rótulos
                  (Serviço / Processo · Período / Referência), strings de setor.js. */}
              {!(dados_basicos.is_servico && isPlaceholderFisico(dados_basicos.produto)) && (
                <div className="info-card">
                  <div className="info-label">{labelsFisicos(dados_basicos.is_servico).produto}</div>
                  <div className="info-value">{dados_basicos.produto || '—'}</div>
                </div>
              )}
              {!(dados_basicos.is_servico && isPlaceholderFisico(dados_basicos.lote)) && (
                <div className="info-card">
                  <div className="info-label">{labelsFisicos(dados_basicos.is_servico).lote}</div>
                  <div className="info-value">{dados_basicos.lote || '—'}</div>
                </div>
              )}
              <div className="info-card">
                <div className="info-label">Data de Ocorrência</div>
                <div className="info-value">{formatDataPtBR(dados_basicos.data_ocorrencia)}</div>
              </div>
              {/* #51 (Sessão 32): custo estimado — só quando preenchido. #financeiro-toggle:
                  + showSection('custo') → o usuário pode DESMARCAR o custo no seletor de
                  seções do PDF (não é relevante pra todo destinatário, ex: cliente reclamante).
                  Default ligado (showSection retorna true quando não há filtro de seções). */}
              {dados_basicos.custo_estimado != null && showSection('custo') && (
                <div className="info-card">
                  <div className="info-label">Custo estimado</div>
                  <div className="info-value">R$ {formatCurrencyBR(dados_basicos.custo_estimado)}</div>
                </div>
              )}
              {/* #279/#280 — Tratamento da saída não conforme (ISO 8.7) + decisão
                  correção × ação corretiva + abrangência. Cada um só aparece
                  quando preenchido (NCs antigas não têm). Compartilha o toggle
                  'disposicao' no seletor de seções do PDF. */}
              {labelDisposicao(dados_basicos.disposicao_saida) && showSection('disposicao') && (
                <div className="info-card">
                  <div className="info-label">Disposição da saída não conforme</div>
                  <div className="info-value">{labelDisposicao(dados_basicos.disposicao_saida)}</div>
                </div>
              )}
              {labelSimNao(dados_basicos.requer_acao_corretiva) && showSection('disposicao') && (
                <div className="info-card">
                  <div className="info-label">Requer ação corretiva</div>
                  <div className="info-value">{labelSimNao(dados_basicos.requer_acao_corretiva)}</div>
                </div>
              )}
              {labelSimNao(dados_basicos.abrangencia_verificada) && showSection('disposicao') && (
                <div className="info-card">
                  <div className="info-label">Abrangência verificada</div>
                  <div className="info-value">{labelSimNao(dados_basicos.abrangencia_verificada)}</div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* 2. Cliente Emissor (origem externa) */}
        {showSection('cliente_emissor') && reportData.origem?.origem_tipo === 'cliente' && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Origem externa — emitida pelo cliente
            </h2>
            <div className="info-grid">
              {reportData.origem.cliente_emissor && (
                <div className="info-card">
                  <div className="info-label">Cliente emissor</div>
                  <div className="info-value">{reportData.origem.cliente_emissor}</div>
                </div>
              )}
              {reportData.origem.numero_nc_cliente && (
                <div className="info-card">
                  <div className="info-label">Nº NC no cliente</div>
                  <div className="info-value">{reportData.origem.numero_nc_cliente}</div>
                </div>
              )}
              {reportData.origem.prazo_resposta_cliente && (
                <div className="info-card">
                  <div className="info-label">Prazo de resposta</div>
                  <div className="info-value">{reportData.origem.prazo_resposta_cliente}</div>
                </div>
              )}
              {reportData.origem.contato_cliente_email && (
                <div className="info-card">
                  <div className="info-label">Contato</div>
                  <div className="info-value">{reportData.origem.contato_cliente_email}</div>
                </div>
              )}
              {reportData.origem.arquivo_original_nome && (
                <div className="info-card">
                  <div className="info-label">Arquivo original</div>
                  <div className="info-value">{reportData.origem.arquivo_original_nome}</div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* 3. Descrição da NC */}
        {showSection('descricao') && dados_basicos.descricao_nc && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Descrição da Não Conformidade
            </h2>
            <div className="card"><MarkdownText text={dados_basicos.descricao_nc} /></div>
          </section>
        )}

        {/* 4. Score de Qualidade */}
        {showSection('score') && av4?.modo_analise !== 'registro' && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Completude da análise
            </h2>
            <div className="card score-block">
              <div className="score-number">{score}<span className="score-max"> / 100</span></div>
              <div className="score-label">
                {score >= 80 ? 'Completa' : score >= 60 ? 'Boa' : score >= 40 ? 'Parcial' : 'Incompleta'}
              </div>
              <p className="score-clarifier" style={{ fontSize: '9pt', lineHeight: 1.4, color: 'var(--rp-text-muted)', margin: '6pt 0 0' }}>
                Mede o quão completa e estruturada está a análise; não garante a correção da causa raiz.
              </p>
            </div>
          </section>
        )}

        {/* 5. Ação de Contenção Imediata (modo investigação) */}
        {showSection('contencao') && av4?.acao_contencao_imediata?.descricao && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Ação de Contenção Imediata
            </h2>
            <div className="card card-danger">
              <p className="card-text"><MarkdownText text={av4.acao_contencao_imediata.descricao} /></p>
              <div className="card-meta-row">
                {av4.acao_contencao_imediata.prazo_horas != null && (
                  <span><strong>Prazo:</strong> {av4.acao_contencao_imediata.prazo_horas}h</span>
                )}
                {av4.acao_contencao_imediata.responsavel_funcao && (
                  <span><strong>Responsável:</strong> {av4.acao_contencao_imediata.responsavel_funcao}</span>
                )}
              </div>
              {av4.acao_contencao_imediata.criterio_conclusao && (
                <p className="card-subtext">
                  <strong>Critério de conclusão:</strong> <MarkdownText text={av4.acao_contencao_imediata.criterio_conclusao} />
                </p>
              )}
            </div>
          </section>
        )}

        {/*
          6. Ação Imediata (legacy / fallback pré-v4)

          Tier 1 Bug 2 fix (Sessão 14): seções 5 e 6 antes não eram
          mutuamente exclusivas. Backend `_map_v4_to_legacy:265-267`
          copia `analysis_v4.acao_contencao_imediata.descricao` para
          `legacy.acao_imediata` por retrocompat — então quando ambos
          os campos vêm preenchidos (NC v4 moderna), ambas as seções
          renderizam o MESMO texto.

          Empírico em RNC002_2026_MULTIELET.pdf v2 e v5: seções 5 e 6
          mostram texto verbatim idêntico ("Bloquear e segregar o
          lote... Identificar instrumentos vencidos...").

          Fix: renderizar seção 6 SOMENTE quando NÃO houver v4 canônico
          (`!av4?.acao_contencao_imediata?.descricao`). Garante:
            - NC v4 moderna: só seção 5 (campo canônico)
            - NC legacy pré-v4: só seção 6 (fallback)
            - Nunca ambas com texto duplicado.

          TD-followup-audit-map-v4-to-legacy-deprecar Sprint 5+ cravado
          pra avaliar deprecação do mapping legacy em `_map_v4_to_legacy`
          ai_service.py:265-267 — tema de produto sobre arquitetura
          retrocompat vs canônica única.
        */}
        {showSection('acao_imediata')
          && analise.acao_imediata
          && !av4?.acao_contencao_imediata?.descricao && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Ação Imediata
            </h2>
            <div className="card card-warning"><MarkdownText text={analise.acao_imediata} /></div>
          </section>
        )}

        {/* 7. Análise dos Porquês */}
        {showSection('porques') && porques.length > 0 && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Análise dos Porquês
            </h2>
            <ol className="porques-list">
              {porques.map((p, i) => (
                <li key={i} className={i === porques.length - 1 ? 'porque-last' : ''}>
                  <strong>{i === 0 ? 'O Problema' : i === porques.length - 1 ? 'Causa Raiz' : `Por quê ${i + 1}?`}:</strong>{' '}
                  {p}
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* 8. Causa Raiz declarada */}
        {showSection('causa_raiz') && analise.causa_raiz && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Declaração da Causa Raiz
            </h2>
            <div className="card card-success"><MarkdownText text={analise.causa_raiz} /></div>
            {/* Badge de confiança/hipótese REMOVIDO do relatório cliente-facing:
                o relatório é exportado pela própria equipe de qualidade (que assina
                a análise); e pra NC de fornecedor a confiança é forçada pra Média/
                Baixa (constante metodológica, não medida de qualidade), então o
                rótulo "hipótese a confirmar" passava ao cliente a falsa impressão
                de trabalho especulativo. O sinal de confiança segue interno. */}
          </section>
        )}

        {/* 9. Análise 6M (Ishikawa) */}
        {showSection('analise_6m') && av4?.compreensao?.analise_6m && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Análise 6M (Ishikawa)
            </h2>
            <div className="ishikawa-grid">
              {[
                ['Mão de obra', av4.compreensao.analise_6m.mao_de_obra, 'mao_de_obra'],
                ['Método', av4.compreensao.analise_6m.metodo, 'metodo'],
                ['Máquina', av4.compreensao.analise_6m.maquina, 'maquina'],
                ['Material', av4.compreensao.analise_6m.material, 'material'],
                ['Medição', av4.compreensao.analise_6m.medicao, 'medicao'],
                ['Meio ambiente', av4.compreensao.analise_6m.meio_ambiente, 'meio_ambiente'],
              ].filter(([, value]) => ramo6MTemConteudo(value)).map(([label, value, key]) => {
                const principal = av4.compreensao.analise_6m.pilar_principal || '';
                const isPrincipal = principal && key.toLowerCase().includes(principal.toLowerCase().split(' ')[0]);
                return (
                  <div key={key} className={`ishikawa-item ${isPrincipal ? 'ishikawa-principal' : ''}`}>
                    <div className="ishikawa-label">{label}</div>
                    <div className="ishikawa-value"><MarkdownText text={value} /></div>
                    {isPrincipal && <div className="ishikawa-badge">⬆ Pilar principal</div>}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* 10. Hipótese Alternativa — TD49: só renderiza quando IA gerou
             hipótese real (declaracao populada). Quando IA não gera (ex.:
             reanálise com causa raiz definida pelo auditor), a seção
             inteira some — antes mostrava "não foi gerada nesta análise"
             que parecia falha técnica em PDF cliente-facing. Confirmado
             por SQL em prod: 8/8 NCs com declaracao=NULL — esconder é
             universalmente seguro. */}
        {showSection('hipotese_alternativa') && av4?.hipotese_alternativa?.declaracao && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Hipótese Alternativa
            </h2>
            <div className="card card-warning">
              <p className="card-text"><strong><MarkdownText text={av4.hipotese_alternativa.declaracao} /></strong></p>
              {av4.hipotese_alternativa.porques?.length > 0 && (
                <ol className="porques-list-compact">
                  {av4.hipotese_alternativa.porques.map((p, i) => (
                    <li key={i}>{formatHipoteseAlternativaPorque(p)}</li>
                  ))}
                </ol>
              )}
              {av4.hipotese_alternativa.por_que_nao_selecionada && (
                <p className="card-subtext">
                  <strong>Por que não selecionada:</strong> <MarkdownText text={av4.hipotese_alternativa.por_que_nao_selecionada} />
                </p>
              )}
            </div>
          </section>
        )}

        {/* 11. Ações Corretivas */}
        {showSection('acoes') && analise.acoes_corretivas?.length > 0 && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Ações Corretivas
            </h2>
            <div className="acoes-grid">
              {analise.acoes_corretivas.map((acao, i) => (
                <div key={i} className="acao-card">
                  <div className="acao-header">
                    <span className="acao-num">Ação {i + 1}</span>
                    {acao.prioridade && (
                      <span className={`badge badge-${cssSlug(acao.prioridade) || 'media'}`}>
                        {acao.prioridade}
                      </span>
                    )}
                  </div>
                  <p className="acao-text"><MarkdownText text={acao.what || acao.acao || acao.descricao || '—'} /></p>
                  <div className="acao-meta-row">
                    {(acao.who || acao.responsavel) && <span><strong>Resp.:</strong> {acao.who || acao.responsavel}</span>}
                    {acao.when && <span><strong>Prazo:</strong> {acao.when}</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 12. Ações Preventivas */}
        {showSection('acoes_preventivas') && av4?.acoes_preventivas?.length > 0 && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Ações Preventivas
            </h2>
            <div className="acoes-grid">
              {av4.acoes_preventivas.map((acao, i) => (
                <div key={i} className="acao-card">
                  <div className="acao-header">
                    <span className="acao-num">Preventiva {i + 1}</span>
                    {acao.prioridade && (
                      <span className={`badge badge-${cssSlug(acao.prioridade) || 'media'}`}>
                        {acao.prioridade}
                      </span>
                    )}
                  </div>
                  <p className="acao-text"><MarkdownText text={acao.acao || acao.descricao || acao.what || '—'} /></p>
                  <div className="acao-meta-row">
                    {(acao.responsavel || acao.responsavel_funcao || acao.who) && (
                      <span><strong>Resp.:</strong> {acao.responsavel || acao.responsavel_funcao || acao.who}</span>
                    )}
                    {(acao.prazo || acao.when) && <span><strong>Prazo:</strong> {acao.prazo || acao.when}</span>}
                    {acao.abrangencia && <span><strong>Abrangência:</strong> {acao.abrangencia}</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 13. Plano 5W2H — uma entrada por ação corretiva. Sessão 41: tier Registro oculta. */}
        {av4?.modo_analise !== 'registro' && showSection('plano_5w2h') && planoLista.length > 0 && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Plano de Ação 5W2H
            </h2>
            {planoLista.map((acao, idx) => (
              <div key={idx} className="card plano-5w2h-block">
                <div className="plano-5w2h-grid">
                  {/* Só renderiza células preenchidas — campos vazios viravam
                      "Não definido" em material cliente-facing. */}
                  {[
                    ['WHAT (O QUÊ)',     acao.what],
                    ['WHY (POR QUÊ)',    acao.why],
                    ['WHO (QUEM)',       acao.who],
                    ['WHEN (QUANDO)',    acao.when],
                    ['WHERE (ONDE)',     acao.where],
                    ['HOW (COMO)',       acao.how],
                    ['HOW MUCH (QUANTO)', acao.how_much],
                  ]
                    .filter(([, value]) => value && value.toString().trim())
                    .map(([label, value]) => (
                    <div key={label} className="plano-5w2h-item">
                      <div className="plano-5w2h-label">{label}</div>
                      <div className="plano-5w2h-value">{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* 13b. Verificação de Eficácia */}
        {showSection('verificacao_eficacia') && av4?.verificacao_eficacia
          && (av4.verificacao_eficacia.criterio || av4.verificacao_eficacia.meta
              || av4.verificacao_eficacia.metodo
              || av4.verificacao_eficacia.prazo_verificacao) && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Verificação de Eficácia
            </h2>
            <div className="card">
              {[
                ['Critério',    av4.verificacao_eficacia.criterio],
                ['Método',      av4.verificacao_eficacia.metodo],
                ['Meta',        av4.verificacao_eficacia.meta],
                ['Prazo',       av4.verificacao_eficacia.prazo_verificacao],
                ['Responsável', av4.verificacao_eficacia.responsavel_funcao],
              ]
                .filter(([, value]) => value && value.toString().trim())
                .map(([label, value]) => (
                  <p key={label} className="card-text" style={{ margin: '0 0 6px' }}>
                    <strong>{label}:</strong> <MarkdownText text={value} />
                  </p>
                ))}
            </div>
          </section>
        )}

        {/* 13c. #283 / #158 (ISO 10.2.1 e2) — Alterações no SGQ + reavaliação de riscos.
            Campos da Avaliação de Eficácia (colunas da NC, via dados_basicos). Aparece só
            quando ao menos um dos "requer_*" foi respondido; herda a visibilidade da
            Verificação de Eficácia no seletor de seções. nextSectionNum() DENTRO da guarda. */}
        {showSection('verificacao_eficacia')
          && (labelSimNao(dados_basicos.requer_alteracao_sgq) || labelSimNao(dados_basicos.requer_reavaliacao_riscos)) && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Alterações no SGQ e Riscos
            </h2>
            <div className="card">
              {[
                ['Necessita alteração no SGQ',           labelSimNao(dados_basicos.requer_alteracao_sgq)],
                ['Documentos do SGQ alterados',          dados_basicos.requer_alteracao_sgq === 'sim' ? dados_basicos.documentos_sgq_alterados : null],
                ['Reavaliação de riscos e oportunidades', labelSimNao(dados_basicos.requer_reavaliacao_riscos)],
                ['Descrição da reavaliação',             dados_basicos.requer_reavaliacao_riscos === 'sim' ? dados_basicos.reavaliacao_riscos_oportunidades : null],
              ]
                .filter(([, value]) => value && value.toString().trim())
                .map(([label, value]) => (
                  <p key={label} className="card-text" style={{ margin: '0 0 6px' }}>
                    <strong>{label}:</strong> <MarkdownText text={value} />
                  </p>
                ))}
            </div>
          </section>
        )}

        {/* 14. Cronograma Gantt — Sessão 41: tier Registro oculta. */}
        {av4?.modo_analise !== 'registro' && showSection('gantt') && ganttRows.length > 0 && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Cronograma de Implementação
            </h2>
            <table className="table">
              <thead>
                <tr>
                  <th>Tarefa</th>
                  <th>Responsável</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th>Investimento</th>
                </tr>
              </thead>
              <tbody>
                {ganttRows.map((task, i) => (
                  <tr key={i}>
                    <td>{task.name || task.tarefa || task.acao || '—'}</td>
                    <td>{task.owner || task.responsavel || '—'}</td>
                    <td>{task.start || task.inicio || '—'}</td>
                    <td>{task.end || task.fim || '—'}</td>
                    <td>{task.cost || task.custo || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* 15. Impacto Potencial */}
        {showSection('impacto') && analise.impacto_potencial && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Impacto Potencial
            </h2>
            <div className="info-grid">
              {analise.impacto_potencial.cliente && (
                <div className="info-card">
                  <div className="info-label">Cliente</div>
                  <div className="info-value"><MarkdownText text={analise.impacto_potencial.cliente} /></div>
                </div>
              )}
              {analise.impacto_potencial.operacional && (
                <div className="info-card">
                  <div className="info-label">Operacional</div>
                  <div className="info-value"><MarkdownText text={analise.impacto_potencial.operacional} /></div>
                </div>
              )}
              {analise.impacto_potencial.financeiro && (
                <div className="info-card">
                  <div className="info-label">Financeiro</div>
                  <div className="info-value"><MarkdownText text={analise.impacto_potencial.financeiro} /></div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* 16. Risco de Recorrência (TD81: esconder se Indeterminado — sem histórico) */}
        {showSection('risco') && analise.risco_recorrencia && analise.risco_recorrencia.nivel !== 'indeterminado' && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Risco de Recorrência
            </h2>
            <div className="card">
              {analise.risco_recorrencia.nivel && (
                <p className="card-text">
                  <strong>Nível:</strong>{' '}
                  <span className={`badge badge-${cssSlug(analise.risco_recorrencia.nivel)}`}>
                    {analise.risco_recorrencia.nivel}
                  </span>
                </p>
              )}
              {analise.risco_recorrencia.justificativa && (
                <p className="card-subtext">
                  <strong>Justificativa:</strong> <MarkdownText text={analise.risco_recorrencia.justificativa} />
                </p>
              )}
            </div>
            {recorrencia?.detectado && (
              <div className="card card-warning" style={{ marginTop: '8px' }}>
                <p className="card-text">
                  <strong>Recorrência detectada</strong>
                  {recorrencia.casos_similares ? ` (${recorrencia.casos_similares} casos similares)` : ''}
                </p>
                {recorrencia.padrao_identificado && (
                  <p className="card-subtext"><strong>Padrão:</strong> <MarkdownText text={recorrencia.padrao_identificado} /></p>
                )}
                {recorrencia.recomendacao && (
                  <p className="card-subtext"><strong>Recomendação:</strong> <MarkdownText text={recorrencia.recomendacao} /></p>
                )}
              </div>
            )}
          </section>
        )}

        {/* 17. Conclusão Executiva */}
        {showSection('conclusao') && analise.conclusao_executiva && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Conclusão Executiva
            </h2>
            <div className="card card-success"><MarkdownText text={analise.conclusao_executiva} /></div>
          </section>
        )}

        {/* 18. Validação e Status (B43-NEW, Sprint 5A A3) */}
        {showSection('validacao') && analise.validacao_status && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Validação e Status
            </h2>
            <div className="card">
              {analise.validacao_status.status_nc && (
                <p className="card-text">
                  <strong>Status da NC:</strong>{' '}
                  <span className={`badge badge-validacao-${(analise.validacao_status.status_nc || 'nova').replace(/_/g, '-')}`}>
                    {VALIDACAO_STATUS_LABEL[analise.validacao_status.status_nc]
                      || (analise.validacao_status.status_nc || 'nova').replace(/_/g, ' ')}
                  </span>
                </p>
              )}
              <div className="validacao-checklist">
                <p className="card-text"><strong>Checklist:</strong></p>
                <ul className="validacao-checklist-list">
                  <li>
                    <span className="check-mark">{analise.validacao_status.revisado_equipe ? '✅' : '⬜'}</span>
                    Revisado pela equipe
                  </li>
                  <li>
                    <span className="check-mark">{analise.validacao_status.ajustes_realizados ? '✅' : '⬜'}</span>
                    Ajustes realizados
                  </li>
                  <li>
                    <span className="check-mark">{analise.validacao_status.aprovado_execucao ? '✅' : '⬜'}</span>
                    Aprovado para execução
                  </li>
                </ul>
              </div>
              {analise.validacao_status.observacoes && (
                <p className="card-subtext">
                  <strong>Observações:</strong> {analise.validacao_status.observacoes}
                </p>
              )}
            </div>
          </section>
        )}

        {/* 19. Evidências Anexadas (B8+B45, Sprint 5A A4) */}
        {showSection('anexos') && Array.isArray(reportData.arquivos) && reportData.arquivos.length > 0 && (
          <section className="printable-section">
            <h2 className="section-title">
              <span className="section-num">{nextSectionNum()}</span> Evidências Anexadas
            </h2>
            <div className="evidencias-list">
              {reportData.arquivos.map((arq, i) => {
                const isImage = arq.filename?.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i);
                // Preferir data_url (persistido no banco, sobrevive a redeploys
                // do Railway) e cair para filepath estático apenas como fallback
                // para NCs antigas. Mesma estratégia do ReportV2 modal.
                const fileUrl = arq.data_url
                  || (arq.filepath ? `${API_URL}/${arq.filepath.replace(/^\.?\/?/, '')}` : null);
                const icon = isImage ? '🖼️'
                  : arq.filename?.match(/\.(pdf)$/i) ? '📄'
                  : arq.filename?.match(/\.(xls|xlsx|csv)$/i) ? '📊'
                  : arq.filename?.match(/\.(doc|docx)$/i) ? '📝' : '📎';
                return (
                  <div key={i} className="evidencia-item">
                    <div className="evidencia-meta">
                      <span className="evidencia-icon">{icon}</span>
                      <div className="evidencia-info">
                        <div className="evidencia-filename">{arq.filename}</div>
                        {arq.uploaded_at && (
                          <div className="evidencia-date">
                            Anexado em: {new Date(arq.uploaded_at).toLocaleDateString('pt-BR')}
                          </div>
                        )}
                      </div>
                    </div>
                    {isImage && fileUrl && (
                      <img
                        src={fileUrl}
                        alt={arq.filename}
                        className="evidencia-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          const msg = arq.has_blob
                            ? '⚠️ Erro ao decodificar imagem.'
                            : '⚠️ Arquivo não disponível neste deploy. Reanexe a evidência se precisar incluí-la no relatório.';
                          e.target.parentElement.insertAdjacentHTML(
                            'beforeend',
                            `<div class="evidencia-error">${msg}</div>`
                          );
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>

      <PrintableSignatures empresa={empresa} />
    </div>
  );
}
